import { useMemo, useState } from "react"
import { AntdButton } from "components"
import { FilePdfOutlined } from "@ant-design/icons"
import { reportService } from "services/"
import { Modal } from "antd"
import { useContext } from "react"
import ShiftEventsRetainContext, { ShiftEventsContextProps } from "../../ShiftEventsContext"
import { PreviousShiftEvent, ShiftInterface } from "@type/workMonitor.types"
import EventSection from "./EventSection"
import { ShiftStatus } from "config/constants"
import IncidentsSection from "./IncidentsSection"
import HighlightsSection from "../Highlights"
import Header from "../Header"
import Footer from "../Footer"

const handleDownloadReport = async (shift: ShiftInterface) => {
	try {
		const res = await reportService.generateShiftSummaryReport(shift)
		const url = window.URL.createObjectURL(new Blob([res.data]))
		const link = document.createElement("a")
		link.href = url
		link.download = `shift-activity-summary-report.pdf`
		link.click()
		window.URL.revokeObjectURL(url)
	} catch (error) {
		console.error(error)
	}
}

interface ShiftModalProps {
	open: boolean
	onClose: () => void
}

const allowedShiftStatus = [
	{ key: "CHECK", value: ShiftStatus.CHECK },
	{ key: "QR", value: ShiftStatus.QR },
	{ key: "INVENTORY", value: ShiftStatus.INVENTORY },
]

const ActivitySummaryModal = ({ open, onClose }: ShiftModalProps) => {
	const { shift } = useContext(ShiftEventsRetainContext) as ShiftEventsContextProps
	if (!shift || !open) return null
	const [isGeneratingSummaryReport, setIsGeneratingSummaryReport] = useState(false)
	const eventMap = new Map<ShiftStatus, PreviousShiftEvent[]>()

	const {
		shiftEvents,
		siteCallsWindow,
		acceptanceCriteria,
		incidents,
		startTime,
		finishTime,
		post,
		location,
		customer,
		date,
		address,
	} = shift

	for (const event of shiftEvents) {
		const { status } = event
		const list = eventMap.get(status) ?? []
		eventMap.set(status, [...list, event])
	}

	const lateCheckThreshold = parseInt(siteCallsWindow.checkCallWindow?.after)

	const highlightsData = useMemo(() => {
		return {
			geoFenceBreachCount: shift.geoFenceBreachCount,
			incidentEvents: shift.incidents,
			checkEvents: eventMap.get(ShiftStatus.CHECK) ?? [],
			qrEvents: eventMap.get(ShiftStatus.QR) ?? [],
			lateCheckThreshold,
		}
	}, [shift, eventMap])

	const headerData = useMemo(() => {
		return {
			title: "SHIFT ACTIVITY REPORT",
			date,
			customer,
			address,
			location,
			employee: shift.employee,
			post,
			startTime,
			finishTime,
			employeeQualification: shift.employeeQualification,
			bookOnEvent: eventMap.get(ShiftStatus.BOOKON) ?? [],
			bookOffEvent: eventMap.get(ShiftStatus.BOOKOFF) ?? [],
		}
	}, [shift, eventMap])
	return (
		<Modal
			title={
				<div className="flex w-full items-center justify-between">
					<span className="text-lg font-semibold">Activity Summary Report</span>

					<div className="mr-5">
						<AntdButton
							className="flex items-center justify-center"
							icon={<FilePdfOutlined />}
							loading={isGeneratingSummaryReport}
							onClick={async () => {
								try {
									setIsGeneratingSummaryReport(true)
									if (!shift?._id) return
									await handleDownloadReport(shift)
								} catch (error) {
									console
								} finally {
									setIsGeneratingSummaryReport(false)
								}
							}}
						>
							Download Report
						</AntdButton>
					</div>
				</div>
			}
			open={open}
			onCancel={onClose}
			footer={null}
			width={1200}
		>
			<div className="font-inter w-full space-y-10 px-6  text-sm text-[#101828] ">
				<Header data={headerData} />

				{allowedShiftStatus.map(({ key, value }) => (
					<EventSection
						key={key}
						status={value}
						events={eventMap.get(value) ?? []}
						lateCheckThreshold={lateCheckThreshold}
						acceptanceCriteria={acceptanceCriteria}
					/>
				))}

				<IncidentsSection incidents={incidents} />
				<HighlightsSection data={highlightsData} />
				<Footer />
			</div>
		</Modal>
	)
}

export default ActivitySummaryModal
